import * as React from 'react';

import { Alert, Box, Button, Paper, Typography } from '@mui/material';

import { MIN_PEERS, type NormSetStats } from './normSetUtils';
import SensorySignature from './SensorySignature';
import type { TokenFamily, TokenSummary } from './tokenProfile';

/**
 * The proposed norm set, before anyone touches it.
 *
 * The ranking is the answer, not a starting point: it is every product the
 * anchors' own peer lists place nearest, which is the comparison the model
 * already made. Editing it is possible and occasionally right, but it is the
 * exception — so the result leads and the editor is a quiet second option.
 *
 * What leads is what the set *tastes* like. Mean distance is how the ranking
 * was made, in arbitrary units with no reference for good or bad; it cannot
 * answer whether this is the right category, so it sits in a caption rather
 * than in the headline it used to hold.
 */

type Props = {
  stats: NormSetStats;
  topN: number;
  editing: boolean;
  onToggleEditing: () => void;
  families: Array<{ family: TokenFamily; tokens: TokenSummary[] }>;
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ minWidth: 96 }}>
    <Typography
      variant="h6"
      sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

const dp = (n: number | null) => (n === null ? '—' : n.toFixed(3));

const NormSetSummary: React.FC<Props> = ({
  stats,
  topN,
  editing,
  onToggleEditing,
  families,
}) => {
  const short = stats.count < MIN_PEERS;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Proposed norm set
      </Typography>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        The {topN} products the anchors rank nearest, which is the comparison
        this model already made. This is normally what you want.
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1.5, mb: 2 }}>
        <Stat label="products" value={String(stats.count)} />
      </Box>

      <SensorySignature families={families} total={stats.count} />

      {short && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {stats.count} products is below the {MIN_PEERS} a norm set needs to
          describe a category rather than a handful of neighbours. Widen the
          ranking or add more anchors.
        </Alert>
      )}

      {/* How the ranking was made, not what it produced. Kept because it is
          occasionally worth checking; demoted because a band of arbitrary
          units cannot say whether this is the right category. */}
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}
      >
        nearest {dp(stats.nearest)} · median {dp(stats.median)} · farthest{' '}
        {dp(stats.farthest)}
        {stats.censored > 0 &&
          ` · ${stats.censored} bounded by an anchor's peer list rather than measured`}
      </Typography>

      {/* Quiet on purpose: the save below is the one accent on this view. */}
      <Button
        size="small"
        onClick={onToggleEditing}
        sx={{ textTransform: 'none', mt: 1 }}
      >
        {editing ? 'Done changing' : 'Change the selection'}
      </Button>
    </Paper>
  );
};

export default NormSetSummary;
